import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { DocumentSenderFieldProviderService } from '../../components/field-selection/document-sender/document-sender-field-provider.service';
import {
  AIASSIST_FIELDS_ENTITY_NAME,
  CONDITIONAL_FIELDS_ENTITY_NAME,
  DOCUMENT_SENDER_ENTITY_NAME,
  IMAGE_FIELDS_ENTITY_NAME,
  SCRIPT_FIELDS_ENTITY_NAME,
} from '../../interfaces/imetadata';
import { Dom } from '../../utils/dom';
import { FieldType as FieldDataType } from '../../utils/fieldtype';
import { Tag, TagType } from '../../utils/tag';
import { templateContext } from '../../utils/template-context';
import { ApplicationStoreService } from '../application-store.service';
import { ContentControlToFieldMapService, SerializedFieldContext } from '../content-control-to-field-map.service';
import { ContentControlId } from '../document-monitors/content-control-id';
import { ContentControlMonitor } from '../document-monitors/content-control-monitor';
import { ContentControlToSignatureFieldService } from '../esignature/content-control-to-signature-field.service';
import { WordService } from '../word.service';
import { LOCALSTORAGE_COPY_KEY } from './copy-key';
import { CopyPasteStorageService } from './copy-paste-storage.service';
import { ContentControlDTO, ControlFieldPairDTO, FieldDTO } from './field-dto';
import { FieldType } from './field-type';
import { InvalidStateEvent, ValidStateChangeEvent, ValidStateEmitter, ValidStateEvent } from './valid-state-emitter';

@Injectable({ providedIn: 'root' })
export class CopyService {
  private copyEmitter: ValidStateEmitter = new ValidStateEmitter();

  private copyActionSubject = new Subject<void>();
  private copyActionImpl$ = this.copyActionSubject.asObservable();

  constructor(
    private applicationStoreService: ApplicationStoreService,
    private contentControlMonitor: ContentControlMonitor,
    private contentControlToFieldMapService: ContentControlToFieldMapService,
    private contentControlToSignatureFieldService: ContentControlToSignatureFieldService,
    private documentSenderFieldProviderService: DocumentSenderFieldProviderService,
    private wordService: WordService,
    private copyPasteStorage: CopyPasteStorageService
  ) {
    contentControlMonitor.contentControlSelectionChanged$.subscribe((selectedContentControls) =>
      this.selectionChanged(selectedContentControls)
    );
  }

  get copyValidStateChange$(): Observable<ValidStateChangeEvent> {
    return this.copyEmitter.validStateChange$;
  }

  get copyAction$(): Observable<void> {
    return this.copyActionImpl$;
  }

  public doCopy(): void {
    (async () => {
      const selection = await this.contentControlMonitor.getSelectedContentControls();
      const contentControlDTOs = await this.getContentControlDTOs(selection);
      const fields = this.buildFieldList(contentControlDTOs);
      const ooxml = await this.getOoxml();

      const fieldExchangeDto = new FieldDTO(
        templateContext.getTargetEntity().namespace,
        templateContext.getTargetEntity().entity,
        this.applicationStoreService.getAddInInfo().currentVersion,
        fields,
        ooxml
      );
      await this.copyPasteStorage.setItem(LOCALSTORAGE_COPY_KEY, fieldExchangeDto.toJson());
      // Writes a dummy value to localStorage to trigger cross-window events, using the date for debugging
      // paste.service.ts has a listener for changes to LOCALSTORAGE_COPY_KEY that updates the clipboard
      localStorage.setItem(LOCALSTORAGE_COPY_KEY, new Date().toISOString());
      this.fireCopyAction();
    })();
  }

  private fireCopyAction(): void {
    this.copyActionSubject.next();
  }

  private async getContentControlDTOs(contentControlIds: ContentControlId[]): Promise<ContentControlDTO[]> {
    return Word.run(async (context) => {
      const allContentControls = await this.wordService.loadContentControls(context);

      const results: ContentControlDTO[] = contentControlIds.map((ccId) => ({
        ...ccId,
        parentId: null,
        childIds: [],
      }));

      for (let i = 0; i < results.length; i++) {
        const ccDto = results[i];
        const cc = allContentControls.find((wcc) => wcc.id === ccDto.id);
        if (!cc) {
          throw new Error(`Failed to find Content Control with id '${ccDto.id}'.`);
        }

        if (!cc.parentContentControlOrNullObject.isNullObject) {
          // only interested if the parent is one of the content controls selected
          const parent = results.find((ccId) => ccId.id === cc.parentContentControlOrNullObject.id);
          if (parent) {
            ccDto.parentId = parent.id;
            parent.childIds.push(ccDto.id);
          }
        }
      }

      return results;
    });
  }

  private buildFieldList(contentControlDTOs: ContentControlDTO[]): ControlFieldPairDTO[] {
    const getFieldType = (dataField: SerializedFieldContext): FieldType => {
      const isEntityField = dataField.entity === templateContext.getTargetEntity().entity;
      if (isEntityField) {
        return FieldType.Entity;
      }

      const isScriptField = dataField.entity === SCRIPT_FIELDS_ENTITY_NAME;
      if (isScriptField) {
        return FieldType.Script;
      }

      const isDocumentSenderField = dataField.entity === DOCUMENT_SENDER_ENTITY_NAME;
      if (isDocumentSenderField) {
        return FieldType.DocumentSender;
      }

      const isImageField = dataField.entity === IMAGE_FIELDS_ENTITY_NAME;
      if (isImageField) {
        return FieldType.Image;
      }

      const isConditionalField = dataField.entity === CONDITIONAL_FIELDS_ENTITY_NAME;
      if (isConditionalField) {
        return FieldType.Conditional;
      }

      const isAiAssistField = dataField.entity === AIASSIST_FIELDS_ENTITY_NAME;
      if (isAiAssistField) {
        return FieldType.AiAssist;
      }

      throw new Error(`Unknown field type '${dataField.entity}'.`);
    };

    const controlFieldPairList: ControlFieldPairDTO[] = [];

    contentControlDTOs.forEach((ccDto) => {
      const dataField = this.contentControlToFieldMapService.get(ccDto.id);
      if (dataField) {
        const fieldType = getFieldType(dataField);
        this.ensureDataType(fieldType, ccDto.tag, dataField);
        controlFieldPairList.push({
          contentControlDto: ccDto,
          field: { type: fieldType, context: { ...dataField } },
        });
        return;
      }

      const signatureField = this.contentControlToSignatureFieldService.get(ccDto.id);
      if (signatureField) {
        controlFieldPairList.push({
          contentControlDto: ccDto,
          field: { type: FieldType.Signature, context: { ...signatureField } },
        });
        return;
      }

      console.warn(`No field matches CCDto: ${ccDto.id} ${ccDto.tag}`);
    });

    return controlFieldPairList;
  }

  private ensureDataType(fieldType: FieldType, tagString: string, dataField: SerializedFieldContext): void {
    if (dataField.dataType) {
      return;
    }

    // legacy fields didn't have the field data type in SerializedFieldContext (i.e. 1.4.0 and earlier)

    console.debug(`Legacy field '${dataField.path}' copied. Determining data type.`);

    if (fieldType === FieldType.Entity) {
      dataField.dataType = templateContext.getFieldByPath(dataField.path)?.type;
    } else if (fieldType === FieldType.Script) {
      // alternatively, we could get the dope xpath, look up the model element, parse the model element into a ThXXXX
      const tag = Tag.fromString(tagString);
      if (tag.type === TagType.Repeat) {
        // od:repeat=x3
        dataField.dataType = FieldDataType.TO_MANY;
      } else if (tag.isKp) {
        // kp:xpath=x2
        dataField.dataType = FieldDataType.TO_ONE;
      } else {
        dataField.dataType = FieldDataType.STRING;
      }
    } else if (fieldType === FieldType.DocumentSender) {
      const field = this.documentSenderFieldProviderService.lookup(dataField.path);
      dataField.dataType = field?.type;
    } else if (fieldType === FieldType.Image) {
      dataField.dataType = dataField.imageFieldConfig?.type;
    }

    if (!dataField.dataType) {
      console.warn(`Assuming data type for field '${fieldType}' is string.`);
      dataField.dataType = FieldDataType.STRING;
    }
  }

  private async getOoxml(): Promise<string> {
    return Word.run(async (context) => {
      const range = context.document.getSelection();
      const ooxml = range.getOoxml();
      await context.sync();
      return ooxml.value;
    });
  }

  private async selectionChanged(contentControls: ContentControlId[]): Promise<void> {
    console.debug('%ccopy-n-paste - content control selection changed handler', 'color:#aaaa00');

    if (contentControls.length === 0) {
      this.copyEmitter.fireValidStateChange(InvalidStateEvent);
      return;
    }

    const ooxml = await this.getOoxml();
    const selectionsMatch = this.checkSelectionsMatch(contentControls, ooxml);

    const copyValidStateEvent = selectionsMatch ? ValidStateEvent : InvalidStateEvent;
    this.copyEmitter.fireValidStateChange(copyValidStateEvent);
  }

  // the content controls in the current selection don't necessarily match the OOXML for the current selection!
  private checkSelectionsMatch(selection: ContentControlId[], ooxml: string): boolean {
    const dom = new Dom(ooxml);
    const tags = dom.documentElement.getElementsByTagName('w:tag');

    const findTag = (tagText: string): boolean => {
      for (let i = 0; i < tags.length; i++) {
        if (tags[i].getAttribute('w:val') === tagText) {
          return true;
        }
      }
      return false;
    };

    for (let i = 0; i < selection.length; i++) {
      const found = findTag(selection[i].tag);
      if (!found) {
        console.debug(`Missing controls from OOXML. ${selection.length} controls selected, ${tags.length} tags.`);
        return false;
      }
    }

    return true;
  }
}
